import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text, Toggle } from '../../../../components';
import { CASINO_SETTINGS, COLORS } from '../../../../theme/tokens';
import type { DisplayLogo } from '../../logos';
import { logoSpec } from '../../logos';
import {
  formatRatio,
  isAllowedLogoFile,
  isBelowResolution,
  isOffRatio,
  prepareLogo,
  type PreparedLogo,
} from '../../logo-image';
import type { ReplacementOptions } from '../../logo-replacement';
import { useAutoFrame } from '../../useAutoFrame';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { UploadGlyph } from '../Glyphs/Glyphs';
import { localizeLogoLabel, localizeSlotDescription } from '../LogosSection/LogosSection';
import {
  HiddenInput,
  Dimmed,
  DropArt,
  DropSheetBack,
  DropSheetFront,
  Target,
  TargetFrame,
  TargetMeta,
  TargetHead,
  TargetLabel,
  CurrentBadge,
  TargetDesc,
  DropZone,
  DropStage,
  DropStageLayer,
  DropLabels,
  DropLabelLayer,
  DropTitle,
  Preview,
  PreviewFrame,
  PreviewImg,
  PreviewMeta,
  FileName,
  FileSize,
  Relink,
  Guidelines,
  Guideline,
  GuidelineText,
  InfoBox,
  InfoText,
  Processing,
  Spinner,
  ProcessingText,
  WarnRow,
  WarnText,
  ErrorRow,
  ErrorText,
  Options,
  Option,
  OptionSwitch,
  OptionText,
  OptionHead,
  OptionMark,
  OptionLabel,
  OptionHint,
  OptionAside,
} from './UploadLogoModal.styles';

/* The multicolour Lumio mark, same asset as the Lumio panel header, the top bar and the
 * customizer's suggestion headers. A gradient asset, so it renders as a plain <img>. */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

/**
 * How long the drag-over heartbeat may go quiet before the overlay treats the drag as
 * gone. Comfortably above the ~350ms the drag-and-drop model re-fires `dragover` at, so a
 * held-still file never falsely cancels.
 */
const DRAG_IDLE_MS = 900;

/**
 * Ceiling on preparing one file. Real work is ~60ms even for a 3000×2000 logo, so this is
 * only ever hit when the browser stops reporting: a FileReader or an <img> that fires
 * neither load nor error leaves a promise that never settles, and the overlay would sit on
 * its spinner for good. Past this it fails into the normal error state instead.
 */
const PREPARE_TIMEOUT_MS = 8000;

/**
 * How long the queue round trip may hold the dialog shut.
 *
 * `busy` vetoes every dismissal route, and there is no request timeout anywhere on this path,
 * so a half-open connection would otherwise leave the owner inside a full-screen modal with
 * the page scroll locked and no way out but a reload. The ceiling expires the VETO, not the
 * request: the upload is a multi-MB body that can legitimately take a long time on a slow
 * uplink, and aborting it would turn a slow success into a failure.
 */
const QUEUE_BUSY_CEILING_MS = 20000;

type UploadError = 'unsupported' | 'unreadable' | 'queue' | null;

export type UploadLogoModalProps = {
  casinoName: string;
  /** The logo being replaced — its slot id drives the canonical size and the guidance. */
  logo: DisplayLogo;
  /**
   * The logos a rebuild would redraw from this one, straight from the pipeline's dependency
   * graph. Only ever counted, never rendered — see the switch below for why the copy does not
   * name them — so this is a list of slot ids rather than labels.
   *
   * EMPTY means the rebuild switch is not shown at all: either nothing derives from this logo,
   * or it is not the primary one (the page asks the rebuild question on the primary logo
   * alone; see `rebuildsFor` there).
   */
  rebuilds: string[];
  onClose: () => void;
  /**
   * Hand the picked artwork to the pipeline. Resolves true once the run is QUEUED — the
   * overlay then closes and the page follows the run — or false if it never got that far.
   */
  onReplaced: (imageDataUri: string, options: ReplacementOptions) => Promise<boolean>;
};

/**
 * Replace-one-logo overlay. The owner picks artwork for a single slot; it is decoded and
 * measured against that slot's canonical size (aspect ratio and resolution, both advisory
 * warnings rather than blocks), then handed back to the page to store.
 *
 * Two switches say what Lumio should do with it, both OFF by default — the owner came to put
 * their own artwork on the site, so nothing they did not ask for happens to it:
 *  - prepare the upload: background out and refitted for where the logo is used (on the
 *    primary logo, redrawn into the standard lockup). Off keeps the file exactly as sent;
 *  - rebuild the logos derived from this one. Asked on the primary logo alone — see
 *    `rebuilds`.
 *
 * NEITHER happens in the browser. Both are answers carried to the branding pipeline, which
 * does the work server-side; the preview here is the owner's own file, unaltered, because
 * that is genuinely all this dialog knows. Replacing is therefore not instant: submitting
 * QUEUES a run and closes, and the new artwork appears on the page when the run finishes
 * (the section shows it as in progress meanwhile).
 *
 * Only opened while the logo-edit window is open (the section disables every Replace
 * control otherwise).
 */
export const UploadLogoModal = ({
  casinoName,
  logo,
  rebuilds,
  onClose,
  onReplaced,
}: UploadLogoModalProps): React.ReactElement => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = React.useState('');
  const [prepared, setPrepared] = React.useState<PreparedLogo | null>(null);
  const [preparing, setPreparing] = React.useState(false);
  const [error, setError] = React.useState<UploadError>(null);
  // The artwork src currently decoded in the preview frame. Drives its fade-in, so the
  // picked file arrives with the frame's plate rather than snapping in ahead of it.
  const [shownSrc, setShownSrc] = React.useState<string | null>(null);
  // Cancel and a finished replace both dismiss through the overlay, so the dialog slides
  // out instead of blinking away.
  const { closeHandle, dismiss } = useSheetDismiss(onClose);

  // Lumio's two jobs, both OFF by default. An owner who opens this dialog is here to put
  // THEIR artwork on the site, and the default answer to "may I redraw it" / "may I redraw
  // the rest of the set from it" should be the one that changes least: the file goes up as
  // supplied, and nothing they did not name is touched. Both are one tap away.
  const [enhance, setEnhance] = React.useState(false);
  const [regenerateVariants, setRegenerateVariants] = React.useState(false);
  // Queueing the run: the picked file is uploaded and the pipeline asked to start. Short
  // (a network round trip, not the run itself), but it must lock the footer so a second
  // press can't queue the same replacement twice.
  const [queueing, setQueueing] = React.useState(false);
  // Set once the queue round trip has taken implausibly long; releases the dismissal veto
  // below without touching the request, which may still land.
  const [queueStalled, setQueueStalled] = React.useState(false);
  React.useEffect(() => {
    if (!queueing) {
      setQueueStalled(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setQueueStalled(true), QUEUE_BUSY_CEILING_MS);
    return () => window.clearTimeout(timer);
  }, [queueing]);
  const locked = queueing && !queueStalled;

  // Guard against writing state after the overlay unmounts (the page closes it on success,
  // and a brand switch unmounts it outright).
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const label = localizeLogoLabel(logo.label, t);
  const canonical = logoSpec(logo.id);
  // Ours wins: it says WHERE the logo is used, which is what decides whether a replacement
  // is a good idea. The record's own description (the pipeline's `alt`) restates what the
  // artwork looks like, so it only stands in for a slot we have no copy for.
  const description = localizeSlotDescription(logo.id, t) || logo.description?.trim() || '';
  // Same auto-toning the page's tiles use: a white wordmark previews on a dark plate, a
  // dark one on a light plate. Applies to BOTH the logo being replaced and the file just
  // picked, so neither is ever previewed invisibly.
  const currentFrame = useAutoFrame(logo.src, logo.frame);
  const pickedFrame = useAutoFrame(prepared?.src ?? '', 'light');
  const source = prepared?.source;
  // Both checks are advisory: a mismatch is called out, never refused.
  const offRatio = Boolean(source && canonical && !prepared?.vector && isOffRatio(source, canonical));
  const lowRes = Boolean(source && canonical && !prepared?.vector && isBelowResolution(source, canonical));

  const openPicker = React.useCallback(() => inputRef.current?.click(), []);

  // Only the newest preparation may write state: flipping the switch (or picking again)
  // while one is still decoding would otherwise let the older run land last and leave the
  // preview showing something other than what the switch says.
  const runRef = React.useRef(0);

  /** Read the picked file so it can be previewed, measured and uploaded. */
  const prepare = React.useCallback(async (file: File): Promise<void> => {
    runRef.current += 1;
    const run = runRef.current;
    setPreparing(true);
    try {
      const result = await Promise.race([
        prepareLogo(file),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('preparation timed out')), PREPARE_TIMEOUT_MS);
        }),
      ]);
      if (!mountedRef.current || run !== runRef.current) return;
      setPrepared(result);
    } catch {
      if (!mountedRef.current || run !== runRef.current) return;
      setPrepared(null);
      setError('unreadable');
    } finally {
      if (run === runRef.current) setPreparing(false);
    }
  }, []);

  /** Take a file the owner supplied, however they supplied it (picker or drag-and-drop). */
  const acceptFile = React.useCallback(
    async (picked: File | null): Promise<void> => {
      setError(null);
      if (!picked) return;
      if (!isAllowedLogoFile(picked)) {
        setPrepared(null);
        setFileName('');
        setError('unsupported');
        return;
      }
      setFileName(picked.name);
      await prepare(picked);
    },
    [prepare],
  );

  const onPick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const picked = event.target.files?.[0] ?? null;
      // Clear the input so picking the SAME file again still fires a change event.
      event.target.value = '';
      void acceptFile(picked);
    },
    [acceptFile],
  );

  // Drag-and-drop, armed across the WHOLE viewport rather than just the well: a file
  // dragged anywhere over the page arms the overlay (the surroundings fade back and the
  // well takes over), and it can be dropped anywhere. Dropping REPLACES whatever is
  // already picked, so swapping files is a second drag rather than a trip through the
  // file dialog.
  //
  // `dragenter` / `dragleave` fire for every element the pointer crosses, so a depth
  // counter is what distinguishes "moved onto a child" from "left the window"; without it
  // the fade flickers on every element boundary. `preventDefault` on drag-over and drop is
  // unconditional — otherwise a stray drop makes the browser NAVIGATE to the file, which
  // would throw away everything the owner has set up in here.
  const [dragging, setDragging] = React.useState(false);
  const acceptRef = React.useRef(acceptFile);
  acceptRef.current = acceptFile;

  React.useEffect(() => {
    const carriesFile = (event: DragEvent): boolean => Array.from(event.dataTransfer?.types ?? []).includes('Files');
    let idle: number | undefined;

    const stop = (): void => {
      window.clearTimeout(idle);
      idle = undefined;
      setDragging(false);
    };

    // `dragover` is a heartbeat: the drag-and-drop model re-fires it every ~350ms while a
    // drag is over the page, even with the pointer still. Silence for longer than that
    // means the drag is gone — dragged back out to the desktop, dropped outside the
    // window, or cancelled with Escape — none of which reliably fire an event we could
    // listen for. Counting dragenter/dragleave pairs instead does NOT work here: arming
    // the overlay swaps its own DOM (the well takes over from the preview), and an element
    // removed mid-drag never fires its dragleave, so the count never returns to zero and
    // the overlay stays stuck in drag state.
    const keepAlive = (): void => {
      window.clearTimeout(idle);
      idle = window.setTimeout(() => setDragging(false), DRAG_IDLE_MS);
    };

    const onOver = (event: DragEvent): void => {
      if (!carriesFile(event)) return;
      // Without this the browser NAVIGATES to a dropped file, throwing away everything
      // the owner has set up in here.
      event.preventDefault();
      setDragging(true);
      keepAlive();
    };
    // A null relatedTarget means the pointer left the window itself (rather than moving
    // between elements), which is the common "dragged it back out" case — react at once
    // instead of waiting out the heartbeat.
    const onLeave = (event: DragEvent): void => {
      if (carriesFile(event) && event.relatedTarget === null) stop();
    };
    const onDrop = (event: DragEvent): void => {
      if (!carriesFile(event)) return;
      event.preventDefault();
      stop();
      void acceptRef.current(event.dataTransfer?.files?.[0] ?? null);
    };

    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', stop);
    return () => {
      window.clearTimeout(idle);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', stop);
    };
  }, []);

  // Neither switch touches the artwork — both are instructions carried to the pipeline —
  // so flipping one has nothing to re-prepare and nothing new to preview.
  const onToggleEnhance = React.useCallback(() => setEnhance((on) => !on), []);
  const onToggleRegenerate = React.useCallback(() => setRegenerateVariants((on) => !on), []);

  /**
   * Queued: the page owns it from here — it shows the run in progress and reports the
   * outcome — so the dialog sees itself out.
   *
   * The dismissal is deferred to an effect rather than called inline, because `busy` (below)
   * vetoes EVERY dismissal route and the Sheet reads it from a ref refreshed during render:
   * clearing the flag and dismissing in the same tick is still refused, and would strand the
   * dialog mounted-but-invisible with its backdrop eating clicks.
   */
  const [queued, setQueued] = React.useState(false);
  const dismissed = React.useRef(false);
  React.useEffect(() => {
    // `dismiss` changes identity with the `onClose` prop, so this effect re-runs on every
    // render; the ref is what keeps the dismissal a one-shot rather than a per-render call.
    if (!queued || dismissed.current) return;
    dismissed.current = true;
    dismiss();
  }, [queued, dismiss]);

  const onSubmit = React.useCallback((): void => {
    if (!prepared || preparing || queueing) return;
    setQueueing(true);
    void (async () => {
      try {
        const accepted = await onReplaced(prepared.src, {
          prepareUpload: enhance,
          // A logo nothing derives from has no rebuild switch, so answer `false` rather than
          // send whatever the hidden state happens to hold: it strands nothing either way,
          // and the request should say what was actually asked for.
          cascade: rebuilds.length > 0 && regenerateVariants,
        });
        if (!mountedRef.current) return;
        setQueueing(false);
        // A refusal keeps the dialog open with the reason.
        if (accepted) setQueued(true);
        else setError('queue');
      } catch {
        // `onReplaced` is documented never to reject, but it is a prop: an implementation that
        // does would otherwise leave `queueing` true forever, with the Replace button dead and
        // the dialog undismissable now that `busy` is wired up.
        if (!mountedRef.current) return;
        setQueueing(false);
        setError('queue');
      }
    })();
  }, [prepared, preparing, queueing, onReplaced, enhance, rebuilds.length, regenerateVariants]);

  // One secondary action and the primary one, at every width: two buttons sit across a
  // phone footer without wrapping or breaking their labels, so there is no overflow menu
  // and no breakpoint branch here.
  const footer = (
    <>
      {/* Disabled rather than merely vetoed by `busy`: a button that silently does nothing
          reads as broken. Same reason the Sheet disables its own close affordance. */}
      <Button label={t('Cancel')} variant="text" disabled={locked} onClick={dismiss} />
      {/* Deliberately NOT disabled while re-preparing: flipping a switch re-runs
          preparation on the file already picked, and blinking the CTA out and back for a
          few hundred ms reads as a glitch. `onSubmit` refuses a stale submit instead. */}
      <Button
        label={queueing ? t('Starting…') : t('Replace logo')}
        variant="primary"
        disabled={!prepared || preparing || queueing}
        onClick={onSubmit}
      />
    </>
  );

  return (
    <Sheet
      // Any logo is replaceable now, not just the primary one, so the title/label are per-logo.
      title={t('Replace logo')}
      ariaLabel={t('Replace {{label}}', { label })}
      onClose={onClose}
      // Busy for the QUEUE round trip only. This dialog does upload again — `onReplaced` stores
      // the artwork and asks casino-creation to start a run, two real network calls — and it is
      // the only surface a refusal can be reported on: dismissed mid-flight, the continuation
      // bails on `mountedRef` and the page's own toast covers accepted-then-failed runs, not
      // refused ones. So the common refusal (another run already in flight for this brand) went
      // unreported and the owner believed their replacement had started.
      //
      // Still NOT gated on `preparing` (~60ms of local image work) — this file says twice over
      // that interaction must not be blocked on that.
      busy={locked}
      footer={footer}
      deepenBackdrop={dragging}
      closeHandle={closeHandle}
    >
      <HiddenInput
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml"
        aria-label={t('Choose a logo file')}
        onChange={onPick}
      />

      <Dimmed $dim={dragging}>
        <Target>
          <TargetFrame $frame={currentFrame}>
            <PreviewImg src={logo.src} alt={t('{{label}} logo', { label })} />
          </TargetFrame>
          <TargetMeta>
            <TargetHead>
              <TargetLabel text={label} tag="span" />
              <CurrentBadge text="Current" tag="span" />
            </TargetHead>
            {description ? <TargetDesc text={description} tag="span" /> : null}
          </TargetMeta>
        </Target>
      </Dimmed>

      {/* The spinner stands in only while there is nothing to show yet. Flipping the
          Lumio switch re-prepares the file already picked, and swapping its preview out
          for a spinner and back would flicker for a job that takes a few hundred ms. */}
      {preparing && !prepared ? (
        <Processing>
          <Spinner aria-hidden="true" />
          <ProcessingText text="Preparing your logo…" tag="span" />
        </Processing>
      ) : prepared && !dragging ? (
        <Preview>
          <PreviewFrame $frame={pickedFrame}>
            <PreviewImg
              src={prepared.src}
              alt={t('{{name}} new logo preview', { name: casinoName })}
              $fade
              $shown={shownSrc === prepared.src}
              // Keyed by src, not a boolean, so picking a second file fades that one in too
              // without a reset effect racing the load. `onError` matters as much as `onLoad`:
              // artwork that never decodes must still be revealed, or the frame sits empty.
              onLoad={() => setShownSrc(prepared.src)}
              onError={() => setShownSrc(prepared.src)}
            />
          </PreviewFrame>
          <PreviewMeta>
            <FileName text={fileName} tag="span" title={fileName} />
            {prepared.vector ? (
              <FileSize text="Vector file · scales to any size" tag="span" />
            ) : (
              <FileSize
                text="{{width}} × {{height}} px · {{ratio}}"
                placeholders={{
                  width: prepared.source.width,
                  height: prepared.source.height,
                  ratio: formatRatio(prepared.source.width, prepared.source.height),
                }}
                tag="span"
              />
            )}
            <Relink type="button" aria-label={t('Choose a different logo file')} onClick={openPicker}>
              <Text text="Choose a different file" tag="span" />
            </Relink>
          </PreviewMeta>
        </Preview>
      ) : (
        <DropZone type="button" aria-label={t('Choose or drop an image')} $dragging={dragging} onClick={openPicker}>
          {/* Both states are always mounted and cross-faded, so the well keeps ONE height
              through arming, dropping and cancelling — nothing to jump. */}
          <DropStage aria-hidden="true">
            <DropStageLayer $on={!dragging}>
              <UploadGlyph size={26} />
            </DropStageLayer>
            <DropStageLayer $on={dragging}>
              <DropArt>
                <DropSheetBack $side="left" />
                <DropSheetBack $side="right" />
                <DropSheetFront $running={dragging} />
              </DropArt>
            </DropStageLayer>
          </DropStage>
          <DropLabels>
            <DropLabelLayer $on={!dragging}>
              <DropTitle text="Choose or drop an image" tag="span" />
            </DropLabelLayer>
            <DropLabelLayer $on={dragging}>
              <DropTitle text="Drop it here" tag="span" />
            </DropLabelLayer>
          </DropLabels>
        </DropZone>
      )}

      <Dimmed $dim={dragging}>
        {offRatio && canonical ? (
          <WarnRow>
            <Icon name="warning" size={14} color={CASINO_SETTINGS.lockFg} />
            <WarnText
              text="This image is a different shape from the {{ratio}} this logo uses, so it will sit with empty space around it wherever the logo appears."
              placeholders={{ ratio: formatRatio(canonical.width, canonical.height) }}
              tag="span"
            />
          </WarnRow>
        ) : null}
        {lowRes && canonical ? (
          <WarnRow>
            <Icon name="warning" size={14} color={CASINO_SETTINGS.lockFg} />
            <WarnText
              text="This image is smaller than the recommended {{width}} × {{height}} px, so it may look soft on sharper screens."
              placeholders={{ width: canonical.width, height: canonical.height }}
              tag="span"
            />
          </WarnRow>
        ) : null}

        {error === 'unsupported' ? (
          <ErrorRow>
            <Icon name="warning" size={16} color={COLORS.statusDanger} />
            <ErrorText text="That file isn't supported. Use a PNG, JPG or SVG under 10 MB." tag="span" />
          </ErrorRow>
        ) : error === 'unreadable' ? (
          <ErrorRow>
            <Icon name="warning" size={16} color={COLORS.statusDanger} />
            <ErrorText text="We couldn't read that image. Try another file." tag="span" />
          </ErrorRow>
        ) : error === 'queue' ? (
          <ErrorRow>
            <Icon name="warning" size={16} color={COLORS.statusDanger} />
            {/* The usual cause is another change already running for this casino: only one
                at a time can rebuild the logo set, so the second would overwrite the first. */}
            <ErrorText
              text="We couldn't start this change. If something else is still being updated, wait for it to finish and try again."
              tag="span"
            />
          </ErrorRow>
        ) : null}

        {/* Never gated on `preparing`: unmounting this half collapses the dialog's scroll
            height, the browser clamps the scroll position, and the owner is thrown back to
            the top of the dialog every time they flip a switch. */}
        {/* The file rule comes first: it is what the owner needs BEFORE picking. Kept
              short, since the size and shape are already stated above on the logo being
              replaced, and the background is covered by the switch below. */}
        <Guidelines>
          <Guideline>
            <Icon name="check" size={14} color={CASINO_SETTINGS.okFg} />
            <GuidelineText text="PNG, JPG or SVG, up to 10 MB · a transparent PNG works best" tag="span" />
          </Guideline>
          {canonical ? (
            <Guideline>
              <Icon name="check" size={14} color={CASINO_SETTINGS.okFg} />
              <GuidelineText
                text="Recommended {{width}} × {{height}} px · {{ratio}}"
                placeholders={{
                  width: canonical.width,
                  height: canonical.height,
                  ratio: formatRatio(canonical.width, canonical.height),
                }}
                tag="span"
              />
            </Guideline>
          ) : null}
        </Guidelines>

        <Options>
          <Option>
            <OptionText>
              <OptionHead>
                <OptionMark src={LUMIO_MARK_SRC} alt="" $muted={!enhance} />
                <OptionLabel text="Let Lumio prepare it" tag="span" />
              </OptionHead>
              <OptionHint text="Removes the background and adapts your logo to fit the places it's used." tag="span" />
              <OptionAside text="Leave it off and your file is used exactly as it is." tag="span" />
            </OptionText>
            <OptionSwitch>
              <Toggle on={enhance} label="Let Lumio prepare it" onToggle={onToggleEnhance} />
            </OptionSwitch>
          </Option>
          {/* Only asked on the primary logo, and only when something actually derives from it.
              The copy says "your other logo variants" rather than listing them: seeded from the
              primary that is the whole family, and a seven-item enumeration mid-sentence is a
              line owners skip — which costs them the one thing the switch is asking about. */}
          {rebuilds.length > 0 ? (
            <Option>
              <OptionText>
                <OptionHead>
                  <OptionMark src={LUMIO_MARK_SRC} alt="" $muted={!regenerateVariants} />
                  <OptionLabel text="Rebuild my other logos from it" tag="span" />
                </OptionHead>
                <OptionHint
                  text="Lumio redraws your other logo variants from this artwork. You can still replace any of them one by one afterwards."
                  tag="span"
                />
                <OptionAside
                  text="Leave it off and they keep their current artwork, which will no longer match."
                  tag="span"
                />
              </OptionText>
              <OptionSwitch>
                <Toggle on={regenerateVariants} label="Rebuild my other logos from it" onToggle={onToggleRegenerate} />
              </OptionSwitch>
            </Option>
          ) : null}
        </Options>

        {/* Not a restatement of the switches above (they already say what happens to
                the rest of the set) — the one thing this overlay has to put in front of an
                owner is that what they upload is on them. */}
        <InfoBox>
          <Icon name="info" size={16} color={CASINO_SETTINGS.lockFg} />
          <InfoText
            text="You're responsible for the artwork you upload. Don't upload anything illegal, or anything you don't hold the rights to use."
            tag="span"
          />
        </InfoBox>
      </Dimmed>
    </Sheet>
  );
};

export default UploadLogoModal;
